import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  NavIcon,
  useBrandTokens,
  PageHeader,
  type ShellOutletContext,
} from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qFormDefinitions,
  qActiveFormByPurpose,
  type FormDefinitionRow,
} from '@helios/data-access';

// ─── Status Badge ───

type FormStatus = 'draft' | 'published' | 'archived';

const STATUS_STYLES: Record<FormStatus, { bg: string; color: string; label: string }> = {
  draft: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', label: 'Draft' },
  published: {
    bg: 'var(--color-success-bg)',
    color: 'var(--color-success-text)',
    label: 'Published',
  },
  archived: { bg: 'var(--color-neutral-bg)', color: 'var(--text-muted)', label: 'Archived' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[(status as FormStatus) ?? 'draft'] ?? STATUS_STYLES.draft;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Column widths ───

const COL_WIDTHS = {
  name: '30%',
  status: '12%',
  version: '10%',
  updated: '20%',
  actions: '28%',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Main Component ───

export function FormsListPage() {
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {};
  const orgId = claims?.activeOrg ?? '';

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: formRows = [] } = usePowerSyncQuery<FormDefinitionRow>(qFormDefinitions(orgId));
  const { data: activeRegRows = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qActiveFormByPurpose(orgId, 'registration'),
  );
  const activeRegistrationFormId = activeRegRows[0]?.id ?? null;

  const filtered = useMemo(() => {
    let rows = formRows;
    if (statusFilter !== 'all') {
      rows = rows.filter((r) => r.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }
    return rows;
  }, [formRows, search, statusFilter]);

  const handleNewForm = useCallback(async () => {
    if (!db || !orgId) return;
    const id = crypto.randomUUID();
    await db.execute(
      `INSERT INTO form_definitions (id, org_id, name, status, version, fields_schema, created_at, updated_at)
       VALUES (?, ?, ?, 'draft', 1, '[]', datetime('now'), datetime('now'))`,
      [id, orgId, 'Untitled Form'],
    );
    navigate(`/clinic/forms/${id}?step=1`);
  }, [db, orgId, navigate]);

  const handleDuplicate = useCallback(
    async (form: FormDefinitionRow, openAfter = false) => {
      if (!db || !orgId) return;
      const id = crypto.randomUUID();
      await db.execute(
        `INSERT INTO form_definitions (id, org_id, name, status, version, fields_schema, created_at, updated_at)
         VALUES (?, ?, ?, 'draft', 1, ?, datetime('now'), datetime('now'))`,
        [id, orgId, `${form.name} (Copy)`, form.fields_schema],
      );
      // Also duplicate pages and mappings
      const pages = await db.getAll<{
        id: string;
        page_number: number;
        image_url: string;
        image_width: number;
        image_height: number;
        label: string | null;
      }>(
        `SELECT * FROM form_template_pages WHERE form_definition_id = ? ORDER BY page_number ASC`,
        [form.id],
      );
      const pageIdMap = new Map<string, string>();
      for (const page of pages) {
        const newPageId = crypto.randomUUID();
        pageIdMap.set(page.id, newPageId);
        await db.execute(
          `INSERT INTO form_template_pages (id, form_definition_id, page_number, image_url, image_width, image_height, label, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
          [
            newPageId,
            id,
            page.page_number,
            page.image_url,
            page.image_width,
            page.image_height,
            page.label,
          ],
        );
      }
      const mappingsRows = await db.getAll<{
        page_id: string;
        field_key: string;
        field_type: string;
        x_pct: number;
        y_pct: number;
        w_pct: number;
        h_pct: number;
        config: string;
        sort_order: number;
      }>(`SELECT * FROM form_template_mappings WHERE form_definition_id = ?`, [form.id]);
      for (const m of mappingsRows) {
        const newPageId = pageIdMap.get(m.page_id) ?? m.page_id;
        await db.execute(
          `INSERT INTO form_template_mappings (id, form_definition_id, page_id, field_key, field_type, x_pct, y_pct, w_pct, h_pct, config, sort_order, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
          [
            crypto.randomUUID(),
            id,
            newPageId,
            m.field_key,
            m.field_type,
            m.x_pct,
            m.y_pct,
            m.w_pct,
            m.h_pct,
            m.config,
            m.sort_order,
          ],
        );
      }
      if (openAfter) {
        navigate(`/clinic/forms/${id}?step=1`);
      }
    },
    [db, orgId, navigate],
  );

  const handlePublish = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_definitions SET status = 'published', updated_at = datetime('now') WHERE id = ?`,
        [form.id],
      );
    },
    [db],
  );

  const handleArchive = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_definitions SET status = 'archived', updated_at = datetime('now') WHERE id = ?`,
        [form.id],
      );
    },
    [db],
  );

  const handleRestore = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_definitions SET status = 'draft', updated_at = datetime('now') WHERE id = ?`,
        [form.id],
      );
    },
    [db],
  );

  const handleDelete = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      // Delete mappings, pages, then definition
      await db.execute(`DELETE FROM form_template_mappings WHERE form_definition_id = ?`, [
        form.id,
      ]);
      await db.execute(`DELETE FROM form_template_pages WHERE form_definition_id = ?`, [form.id]);
      await db.execute(`DELETE FROM form_definitions WHERE id = ?`, [form.id]);
    },
    [db],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <PageHeader
        title="Forms"
        subtitle="Manage form templates for patient documents"
        collapsed={collapsed}
        onToggle={onToggleSidebar}
      >
        <button
          onClick={handleNewForm}
          style={{
            background: primary,
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '0 16px',
            height: 38,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <NavIcon name="plus" />
          New Form
        </button>
      </PageHeader>

      {/* Search + Filters */}
      <div
        style={{
          padding: '0 var(--sp-5) var(--sp-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ position: 'relative', flex: 1 }}>
          <span
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
            }}
          >
            <NavIcon name="search" />
          </span>
          <input
            type="text"
            placeholder="Search forms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 38,
              paddingLeft: 36,
              paddingRight: 12,
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            height: 38,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            padding: '0 12px',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 var(--sp-5) var(--sp-5)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: COL_WIDTHS.name }} />
            <col style={{ width: COL_WIDTHS.status }} />
            <col style={{ width: COL_WIDTHS.version }} />
            <col style={{ width: COL_WIDTHS.updated }} />
            <col style={{ width: COL_WIDTHS.actions }} />
          </colgroup>
          <thead>
            <tr>
              {['Name', 'Status', 'Version', 'Last Updated', 'Actions'].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid var(--border)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((form) => (
              <tr
                key={form.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.background = '';
                }}
              >
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                  onClick={() => {
                    if (form.status === 'published') {
                      handleDuplicate(form, true);
                    } else if (form.status === 'draft') {
                      navigate(`/clinic/forms/${form.id}?step=1`);
                    }
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {form.name}
                    {form.purpose === 'registration' &&
                      (() => {
                        const isActive = form.id === activeRegistrationFormId;
                        return (
                          <span
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 'var(--font-weight-medium)',
                              color: isActive ? '#16a34a' : '#9ca3af',
                              background: isActive ? '#f0fdf4' : '#f9fafb',
                              border: `1px solid ${isActive ? '#bbf7d0' : '#e5e7eb'}`,
                              borderRadius: 'var(--radius-sm)',
                              padding: '1px 6px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Registration {isActive ? '(Active)' : '(Inactive)'}
                          </span>
                        );
                      })()}
                  </div>
                  {form.description && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                        marginTop: 2,
                      }}
                    >
                      {form.description}
                    </div>
                  )}
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <StatusBadge status={form.status} />
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--text)',
                  }}
                >
                  v{form.version}
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {formatDate(form.updated_at)}
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ display: 'flex', gap: 6 }}>
                    {form.status === 'draft' && (
                      <>
                        <ActionButton
                          label="Edit"
                          onClick={() => navigate(`/clinic/forms/${form.id}?step=1`)}
                        />
                        <ActionButton label="Publish" onClick={() => handlePublish(form)} />
                        <ActionButton
                          label="Delete"
                          onClick={() => handleDelete(form)}
                          variant="danger"
                        />
                      </>
                    )}
                    {form.status === 'published' && (
                      <>
                        <ActionButton
                          label="Edit (Copy)"
                          onClick={() => handleDuplicate(form, true)}
                        />
                        <ActionButton label="Archive" onClick={() => handleArchive(form)} />
                      </>
                    )}
                    {form.status === 'archived' && (
                      <>
                        <ActionButton label="Restore" onClick={() => handleRestore(form)} />
                        <ActionButton label="Duplicate" onClick={() => handleDuplicate(form)} />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: 40,
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {formRows.length === 0
                    ? 'No forms yet. Click "New Form" to create one.'
                    : 'No forms match your filter.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  variant,
}: {
  label: string;
  onClick: () => void;
  variant?: 'danger';
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        height: 28,
        padding: '0 10px',
        borderRadius: 'var(--radius-sm)',
        border: variant === 'danger' ? '1px solid #fecaca' : '1px solid var(--border)',
        background: variant === 'danger' ? '#fef2f2' : 'var(--bg-surface)',
        color: variant === 'danger' ? '#dc2626' : 'var(--text)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}
